import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Layout } from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Progress } from '../components/ui/progress';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '../components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { toast } from 'sonner';
import { Upload, FileText, Check, ChevronRight, ChevronLeft, AlertCircle, Loader2, Download, Edit, Save, ChevronsUpDown, Mail } from 'lucide-react';
import { BulkReviewStep } from '../components/BulkReviewStep';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function BulkResultItem({ r }) {
  var ed = r.extracted_data || {};
  var name = r.success ? (ed.customer_name || 'Unknown') : '';
  var nmi = r.success ? (ed.nmi || 'N/A') : '';
  return (
    <div className={'flex items-center gap-2 text-sm ' + (r.success ? 'text-green-700' : 'text-red-700')}>
      {r.success ? <Check className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
      {r.success ? (
        <>
          <span className="font-mono font-semibold">NMI: {nmi}</span>
          <span className="text-slate-500">— {name}</span>
          <span className="text-slate-400 text-xs">({r.filename})</span>
        </>
      ) : (
        <>
          <span>{r.filename}</span>
          <span>— {r.error}</span>
        </>
      )}
    </div>
  );
}

function BulkResultsList({ results }) {
  return (
    <div className="space-y-1">
      {results.map(function(r, i) { return <BulkResultItem key={i} r={r} />; })}
    </div>
  );
}


const STEPS = [
  { id: 1, title: 'Upload Bill', description: 'Upload customer PDF' },
  { id: 2, title: 'Review Data', description: 'Verify extracted info' },
  { id: 3, title: 'Select Rates', description: 'Choose tariff & plan' },
  { id: 4, title: 'Generate Report', description: 'View results' },
];

const getConfidenceClass = (c) => c >= 0.7 ? 'confidence-high' : c >= 0.4 ? 'confidence-medium' : 'confidence-low';
const getConfidenceText = (c) => c >= 0.7 ? 'High' : c >= 0.4 ? 'Medium' : 'Low';

export default function NewComparison() {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  
  // Step 1: Upload
  const [file, setFile] = useState(null);
  const [uploadResult, setUploadResult] = useState(null);
  
  // Step 2: Review
  const [extractedData, setExtractedData] = useState(null);
  const [editedData, setEditedData] = useState(null);
  const [editing, setEditing] = useState(false);
  
  // Step 3: Tariff + Plan selection
  const [tariffs, setTariffs] = useState([]);
  const [plans, setPlans] = useState([]);
  const [selectedTariff, setSelectedTariff] = useState('');
  const [selectedPlan, setSelectedPlan] = useState('');
  const [includeDemand, setIncludeDemand] = useState(false);
  
  // Retailer discount settings
  const [retailerDiscountAmount, setRetailerDiscountAmount] = useState(0);
  const [retailerDiscountScope, setRetailerDiscountScope] = useState('usage_and_supply');
  
  // Solar & Greenpower (stored in c/kWh)
  const [nbeSolarRate, setNbeSolarRate] = useState(0);
  const [includeGreenpower, setIncludeGreenpower] = useState(false);
  const [greenpowerRate, setGreenpowerRate] = useState(0);
  
  // Metering charge
  const [includeMetering, setIncludeMetering] = useState(false);
  const [meteringChargeMonthly, setMeteringChargeMonthly] = useState(77);
  
  const [tariffOpen, setTariffOpen] = useState(false);
  
  // Bulk upload
  const [bulkMode, setBulkMode] = useState(false);
  const [bulkFiles, setBulkFiles] = useState([]);
  const [bulkResults, setBulkResults] = useState(null);
  const [bulkUploading, setBulkUploading] = useState(false);
  const [bulkStep, setBulkStep] = useState('upload'); // 'upload' | 'review' | 'rates' | 'creating' | 'done'
  const [bulkProgress, setBulkProgress] = useState(0);
  const [bulkComparisons, setBulkComparisons] = useState([]);
  const [bulkSelections, setBulkSelections] = useState({}); // per-bill tariff/plan: { [bill_id]: { tariff, plan } }
  const [bulkEditedData, setBulkEditedData] = useState({}); // per-bill edited data: { [bill_id]: {...} }
  
  // Step 4: Result
  const [comparisonResult, setComparisonResult] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [tariffsRes, plansRes] = await Promise.all([
          axios.get(`${API_URL}/api/tariffs?authorization=${token}`),
          axios.get(`${API_URL}/api/plans?authorization=${token}`)
        ]);
        setTariffs(tariffsRes.data);
        setPlans(plansRes.data);
        if (tariffsRes.data.length > 0) setSelectedTariff(tariffsRes.data[0].id);
        if (plansRes.data.length > 0) setSelectedPlan(plansRes.data[0].id);
      } catch (err) {
        console.error('Failed to fetch tariffs/plans:', err);
      }
    };
    fetchData();
  }, [token]);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
    const files = Array.from(e.dataTransfer.files);
    if (bulkMode) {
      const pdfs = files.filter(f => f.type === 'application/pdf' || f.type.startsWith('image/'));
      if (pdfs.length > 0) setBulkFiles(prev => [...prev, ...pdfs]);
      else toast.error('Please upload PDF files');
    } else {
      const f = files[0];
      if (f?.type === 'application/pdf' || f?.type?.startsWith('image/')) setFile(f);
      else toast.error('Please upload a PDF file');
    }
  }, [bulkMode]);

  const handleBulkUpload = async () => {
    if (bulkFiles.length === 0) { toast.error('Please add PDF files'); return; }
    setBulkUploading(true);
    try {
      const formData = new FormData();
      bulkFiles.forEach(f => formData.append('files', f));
      const res = await axios.post(`${API_URL}/api/bills/upload-bulk?authorization=${token}`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      setBulkResults(res.data);
      toast.success(`${res.data.successful}/${res.data.total} bills processed`);
      if (res.data.successful > 0) {
        // Initialize edited data from extracted data
        const initialEdited = {};
        res.data.results.filter(r => r.success).forEach(r => {
          initialEdited[r.bill_id] = { ...(r.extracted_data || {}) };
        });
        setBulkEditedData(initialEdited);
        setBulkStep('review');
      }
    } catch (err) {
      toast.error('Bulk upload failed');
    } finally { setBulkUploading(false); }
  };

  const createBulkComparisons = async () => {
    const successfulBills = bulkResults.results.filter(r => r.success);
    if (successfulBills.length === 0) { toast.error('No successful bills to compare'); return; }
    // Check all bills have selections
    const missing = successfulBills.filter(b => !bulkSelections[b.bill_id]?.tariff || !bulkSelections[b.bill_id]?.plan);
    if (missing.length > 0) { toast.error(`Please select tariff & plan for all bills (${missing.length} missing)`); return; }
    setBulkStep('creating');
    setBulkProgress(0);
    const created = [];
    for (let i = 0; i < successfulBills.length; i++) {
      const bill = successfulBills[i];
      const sel = bulkSelections[bill.bill_id];
      try {
        const formData = new FormData();
        const editedBillData = bulkEditedData[bill.bill_id] || bill.extracted_data;
        formData.append('bill_file_name', bill.filename);
        formData.append('bill_file_path', bill.bill_id || '');
        formData.append('extracted_data', JSON.stringify(bill.extracted_data));
        formData.append('edited_data', JSON.stringify(editedBillData));
        formData.append('tariff_id', sel.tariff);
        formData.append('plan_id', sel.plan);
        formData.append('retailer_discount_amount', editedBillData.discount_amount || 0);
        formData.append('retailer_discount_scope', editedBillData.discount_scope || 'usage_and_supply');
        formData.append('include_demand', false);
        formData.append('nbe_solar_rate', 0);
        formData.append('greenpower_rate', 0);
        formData.append('include_greenpower', false);
        formData.append('metering_charge_monthly', 0);
        const res = await axios.post(`${API_URL}/api/comparisons?authorization=${token}`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
        created.push({ filename: bill.filename, nmi: (bill.extracted_data || {}).nmi || 'N/A', success: true, comparison: res.data });
      } catch (err) {
        created.push({ filename: bill.filename, nmi: (bill.extracted_data || {}).nmi || 'N/A', success: false, error: err.response?.data?.detail || 'Failed' });
      }
      setBulkProgress(Math.round(((i + 1) / successfulBills.length) * 100));
    }
    setBulkComparisons(created);
    setBulkStep('done');
    const successCount = created.filter(c => c.success).length;
    toast.success(`${successCount}/${successfulBills.length} comparisons created`);
  };

  const applyToAllBills = (field, value) => {
    if (!bulkResults) return;
    const successfulBills = bulkResults.results.filter(r => r.success);
    setBulkSelections(prev => {
      const updated = { ...prev };
      successfulBills.forEach(b => {
        if (!updated[b.bill_id]) updated[b.bill_id] = { tariff: '', plan: '' };
        updated[b.bill_id][field] = value;
      });
      return updated;
    });
  };

  const handleBulkFieldChange = (billId, fieldKey, value) => {
    setBulkEditedData(prev => ({
      ...prev,
      [billId]: { ...prev[billId], [fieldKey]: value }
    }));
  };

  const handleFileSelect = (e) => e.target.files[0] && setFile(e.target.files[0]);

  const uploadBill = async () => {
    if (!file) { toast.error('Please select a file'); return; }
    setLoading(true);
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await axios.post(`${API_URL}/api/bills/upload?authorization=${token}`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      setUploadResult(res.data);
      setExtractedData(res.data.extracted_data);
      setEditedData({ ...res.data.extracted_data });
      // Set retailer discount from extracted data
      setRetailerDiscountAmount(res.data.extracted_data.discount_amount || 0);
      setRetailerDiscountScope(res.data.extracted_data.discount_scope || 'usage_and_supply');
      toast.success('Bill uploaded and processed!');
      setStep(2);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to process bill');
    } finally {
      setLoading(false);
    }
  };

  const handleFieldChange = (field, value) => setEditedData(prev => ({ ...prev, [field]: value }));

  const generateComparison = async () => {
    if (!selectedTariff || !selectedPlan) { toast.error('Please select a tariff and plan'); return; }
    setLoading(true);
    const formData = new FormData();
    formData.append('bill_file_name', uploadResult.file_name);
    formData.append('bill_file_path', uploadResult.file_path);
    formData.append('extracted_data', JSON.stringify(extractedData));
    formData.append('edited_data', JSON.stringify(editedData));
    formData.append('tariff_id', selectedTariff);
    formData.append('plan_id', selectedPlan);
    formData.append('retailer_discount_amount', retailerDiscountAmount);
    formData.append('retailer_discount_scope', retailerDiscountScope);
    formData.append('include_demand', includeDemand);
    formData.append('nbe_solar_rate', nbeSolarRate / 100);
    formData.append('greenpower_rate', greenpowerRate / 100);
    formData.append('include_greenpower', includeGreenpower);
    formData.append('metering_charge_monthly', includeMetering ? meteringChargeMonthly : 0);
    try {
      const res = await axios.post(`${API_URL}/api/comparisons?authorization=${token}`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      setComparisonResult(res.data);
      toast.success('Comparison generated!');
      setStep(4);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to generate comparison');
    } finally {
      setLoading(false);
    }
  };

  const downloadReport = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/comparisons/${comparisonResult.id}/report?authorization=${token}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `NBE_Comparison_${comparisonResult.report_id}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Report downloaded!');
    } catch (err) {
      toast.error('Failed to download report');
    }
  };

  const emailComparison = async () => {
    // Download PDF first
    await downloadReport();
    // Build email content
    const result = comparisonResult?.comparison_result;
    const data = comparisonResult?.edited_data || comparisonResult?.extracted_data || {};
    const subject = `Next Business Energy - Bill Comparison for ${data.customer_name || 'Customer'} (${comparisonResult.report_id})`;
    const body = [
      `Hi,`,
      ``,
      `Please find attached the electricity bill comparison report for:`,
      ``,
      `Customer: ${data.customer_name || 'N/A'}`,
      `NMI: ${data.nmi || 'N/A'}`,
      `Site: ${data.site_address || 'N/A'}`,
      ``,
      `--- Summary ---`,
      `Current Retailer Total: ${formatCurrency(result?.retailer_total)}`,
      `NBE Offer Total: ${formatCurrency(result?.our_total)}`,
      `Estimated Savings: ${formatCurrency(result?.savings_amount)} (${(result?.savings_percentage || 0).toFixed(1)}%)`,
      `Estimated Annual Savings: ${formatCurrency((result?.savings_amount || 0) * 4)}`,
      ``,
      `Report ID: ${comparisonResult.report_id}`,
      ``,
      `Please attach the downloaded PDF report to this email.`,
      ``,
      `Kind regards,`,
      `Next Business Energy`,
    ].join('\n');
    window.location.href = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };

  const formatCurrency = (a) => new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD' }).format(a || 0);

  const renderStep1 = () => (
    <div className="space-y-6">
      <div className="flex items-center gap-4 mb-2">
        <Button variant={!bulkMode ? 'default' : 'outline'} size="sm" onClick={() => setBulkMode(false)} data-testid="single-upload-tab">Single Bill</Button>
        <Button variant={bulkMode ? 'default' : 'outline'} size="sm" onClick={() => setBulkMode(true)} data-testid="bulk-upload-tab">Bulk Upload</Button>
      </div>

      {!bulkMode ? (
        <>
          <div className={`upload-zone ${dragOver ? 'dragover' : ''}`} onDragOver={(e) => { e.preventDefault(); setDragOver(true); }} onDragLeave={() => setDragOver(false)} onDrop={handleDrop} onClick={() => document.getElementById('file-input').click()} data-testid="upload-zone">
            <input id="file-input" type="file" accept=".pdf,.jpg,.jpeg,.png,.webp,.tiff,.tif,.bmp,.heic" className="hidden" onChange={handleFileSelect} />
            <Upload className="w-12 h-12 text-slate-400 mx-auto mb-4" />
            <p className="text-lg font-medium text-slate-900">{file ? file.name : 'Drop your electricity bill here'}</p>
            <p className="text-sm text-slate-500 mt-2">or click to browse (PDF or image, max 10MB)</p>
          </div>
          {file && (
            <div className="flex items-center gap-3 p-4 bg-green-50 rounded-lg border border-green-200">
              <Check className="w-5 h-5 text-green-600" />
              <div className="flex-1"><p className="font-medium text-green-900">{file.name}</p><p className="text-sm text-green-700">{(file.size / 1024).toFixed(1)} KB</p></div>
              <Button onClick={() => setFile(null)} variant="ghost" size="sm" className="text-green-700">Remove</Button>
            </div>
          )}
          <Button onClick={uploadBill} disabled={!file || loading} className="w-full rounded-full bg-primary hover:bg-primary/90 h-12" data-testid="upload-btn">
            {loading ? <><Loader2 className="w-5 h-5 mr-2 animate-spin" />Processing...</> : <>Process Bill<ChevronRight className="w-5 h-5 ml-2" /></>}
          </Button>
        </>
      ) : (
        <>
          <div className={`upload-zone ${dragOver ? 'dragover' : ''}`} onDragOver={(e) => { e.preventDefault(); setDragOver(true); }} onDragLeave={() => setDragOver(false)} onDrop={handleDrop} onClick={() => document.getElementById('bulk-file-input').click()} data-testid="bulk-upload-zone">
            <input id="bulk-file-input" type="file" accept=".pdf,.jpg,.jpeg,.png,.webp,.tiff,.tif,.bmp,.heic" multiple className="hidden" onChange={(e) => setBulkFiles(prev => [...prev, ...Array.from(e.target.files).filter(f => f.type === 'application/pdf' || f.type.startsWith('image/'))])} />
            <Upload className="w-12 h-12 text-slate-400 mx-auto mb-4" />
            <p className="text-lg font-medium text-slate-900">Drop multiple bills here</p>
            <p className="text-sm text-slate-500 mt-2">or click to browse (PDF or images)</p>
          </div>
          {bulkFiles.length > 0 && (
            <div className="space-y-2">
              {bulkFiles.map((f, i) => (
                <div key={i} className="flex items-center gap-3 p-3 bg-green-50 rounded-lg border border-green-200">
                  <FileText className="w-4 h-4 text-green-600" />
                  <span className="flex-1 text-sm text-green-900">{f.name}</span>
                  <Button variant="ghost" size="sm" onClick={() => setBulkFiles(bulkFiles.filter((_, j) => j !== i))} className="text-green-700 h-6 px-2">Remove</Button>
                </div>
              ))}
              <p className="text-sm text-slate-500">{bulkFiles.length} file(s) selected</p>
            </div>
          )}
          <Button onClick={handleBulkUpload} disabled={bulkFiles.length === 0 || bulkUploading} className="w-full rounded-full bg-primary hover:bg-primary/90 h-12" data-testid="bulk-upload-btn">
            {bulkUploading ? <><Loader2 className="w-5 h-5 mr-2 animate-spin" />Processing {bulkFiles.length} bills...</> : <>Process {bulkFiles.length} Bills<ChevronRight className="w-5 h-5 ml-2" /></>}
          </Button>
          {bulkResults && bulkStep === 'upload' && (
            <Card className="shadow-sm border-blue-200">
              <CardContent className="p-4 space-y-3">
                <p className="font-medium text-blue-800">Bulk Upload Results: {bulkResults.successful}/{bulkResults.total} successful</p>
                <BulkResultsList results={bulkResults.results} />
                {bulkResults.successful > 0 && (
                  <Button onClick={() => setBulkStep('review')} className="w-full rounded-full bg-primary hover:bg-primary/90 h-11 mt-3" data-testid="bulk-continue-btn">
                    Continue — Review & Edit Data<ChevronRight className="w-5 h-5 ml-2" />
                  </Button>
                )}
              </CardContent>
            </Card>
          )}
          {bulkResults && bulkStep === 'review' && (
            <BulkReviewStep
              bulkResults={bulkResults}
              bulkEditedData={bulkEditedData}
              onBulkFieldChange={handleBulkFieldChange}
              onBack={() => setBulkStep('upload')}
              onContinue={() => setBulkStep('rates')}
            />
          )}
          {bulkResults && bulkStep === 'rates' && (
            <div className="space-y-4 mt-4">
              <Card className="border-primary/30">
                <CardHeader className="pb-3"><CardTitle className="text-base">Select Tariff & Plan Per Invoice</CardTitle><CardDescription>Each bill requires a tariff and plan before comparison. Use "Apply to all" for quick selection, or set each individually by NMI.</CardDescription></CardHeader>
                <CardContent className="space-y-4">
                  {/* Apply to all controls */}
                  <div className="p-3 bg-slate-50 rounded-lg space-y-2">
                    <p className="text-xs font-medium text-slate-600">Apply to all bills:</p>
                    <div className="flex gap-2">
                      {tariffs.length > 0 && (
                        <Select onValueChange={function(v) { applyToAllBills('tariff', v); }}>
                          <SelectTrigger className="h-9 text-xs flex-1" data-testid="bulk-apply-tariff"><SelectValue placeholder="Set tariff for all" /></SelectTrigger>
                          <SelectContent>{tariffs.map(function(t) { return <SelectItem key={t.id} value={t.id}><span className="font-mono mr-1">{t.tariff_code}</span>{t.tariff_name}</SelectItem>; })}</SelectContent>
                        </Select>
                      )}
                      {plans.length > 0 && (
                        <Select onValueChange={function(v) { applyToAllBills('plan', v); }}>
                          <SelectTrigger className="h-9 text-xs flex-1" data-testid="bulk-apply-plan"><SelectValue placeholder="Set plan for all" /></SelectTrigger>
                          <SelectContent>{plans.map(function(p) { return <SelectItem key={p.id} value={p.id}>{p.plan_name} ({p.discount_percentage}%)</SelectItem>; })}</SelectContent>
                        </Select>
                      )}
                    </div>
                  </div>
                  {/* Per-bill selections */}
                  <div className="space-y-3">
                    {bulkResults.results.filter(function(r) { return r.success; }).map(function(bill) {
                      var ed = bulkEditedData[bill.bill_id] || bill.extracted_data || {};
                      var sel = bulkSelections[bill.bill_id] || { tariff: '', plan: '' };
                      return (
                        <div key={bill.bill_id} className="border rounded-lg p-3 space-y-2" data-testid={'bulk-bill-' + bill.bill_id}>
                          <div className="flex items-center gap-2 text-sm">
                            <FileText className="w-4 h-4 text-primary" />
                            <span className="font-mono font-semibold text-primary">NMI: {ed.nmi || 'N/A'}</span>
                            <span className="text-slate-600 font-medium">{ed.customer_name || 'Unknown'}</span>
                            <span className="text-slate-400 text-xs ml-auto">({bill.filename})</span>
                          </div>
                          <div className="flex gap-2">
                            <Select value={sel.tariff} onValueChange={function(v) { setBulkSelections(function(prev) { return { ...prev, [bill.bill_id]: { ...prev[bill.bill_id], tariff: v, plan: (prev[bill.bill_id] || {}).plan || '' } }; }); }}>
                              <SelectTrigger className="h-8 text-xs flex-1"><SelectValue placeholder="Select tariff" /></SelectTrigger>
                              <SelectContent>{tariffs.map(function(t) { return <SelectItem key={t.id} value={t.id}><span className="font-mono mr-1">{t.tariff_code}</span>{t.tariff_name}</SelectItem>; })}</SelectContent>
                            </Select>
                            <Select value={sel.plan} onValueChange={function(v) { setBulkSelections(function(prev) { return { ...prev, [bill.bill_id]: { ...prev[bill.bill_id], plan: v, tariff: (prev[bill.bill_id] || {}).tariff || '' } }; }); }}>
                              <SelectTrigger className="h-8 text-xs flex-1"><SelectValue placeholder="Select plan" /></SelectTrigger>
                              <SelectContent>{plans.map(function(p) { return <SelectItem key={p.id} value={p.id}>{p.plan_name} ({p.discount_percentage}%)</SelectItem>; })}</SelectContent>
                            </Select>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <div className="flex gap-3 pt-2">
                    <Button variant="outline" onClick={function() { setBulkStep('review'); }} className="flex-1"><ChevronLeft className="w-4 h-4 mr-1" />Back</Button>
                    <Button onClick={createBulkComparisons} className="flex-1 bg-primary hover:bg-primary/90" data-testid="bulk-create-btn">Create {bulkResults.successful} Comparison{bulkResults.successful > 1 ? 's' : ''}<ChevronRight className="w-4 h-4 ml-1" /></Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
          {bulkStep === 'creating' && (
            <div className="space-y-4 mt-4">
              <Card><CardContent className="p-6 text-center space-y-4">
                <Loader2 className="w-10 h-10 animate-spin text-primary mx-auto" />
                <p className="font-medium text-slate-900">Creating comparisons...</p>
                <Progress value={bulkProgress} className="h-2" />
                <p className="text-sm text-slate-500">{bulkProgress}% complete</p>
              </CardContent></Card>
            </div>
          )}
          {bulkStep === 'done' && (
            <div className="space-y-4 mt-4">
              <Card className="border-green-200">
                <CardContent className="p-5 space-y-3">
                  <div className="flex items-center gap-2 text-green-800 font-medium"><Check className="w-5 h-5" />{bulkComparisons.filter(c => c.success).length} comparison{bulkComparisons.filter(c => c.success).length !== 1 ? 's' : ''} created</div>
                  <div className="space-y-1">
                    {bulkComparisons.map((c, i) => (
                      <div key={i} className={'flex items-center gap-2 text-sm ' + (c.success ? 'text-green-700' : 'text-red-700')}>
                        {c.success ? <Check className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
                        <span className="font-mono font-semibold">NMI: {c.nmi}</span>
                        {c.success && c.comparison?.report_id && <span className="text-slate-500 font-mono">— {c.comparison.report_id}</span>}
                        {!c.success && <span>— {c.error}</span>}
                        <span className="text-slate-400 text-xs ml-auto">({c.filename})</span>
                      </div>
                    ))}
                  </div>
                  <div className="flex gap-3 pt-2">
                    <Button variant="outline" onClick={() => { setBulkResults(null); setBulkStep('upload'); setBulkFiles([]); setBulkComparisons([]); setBulkEditedData({}); setBulkSelections({}); }} className="flex-1">Upload More</Button>
                    <Button onClick={() => navigate('/history')} className="flex-1 bg-primary hover:bg-primary/90" data-testid="bulk-view-history-btn">View Comparisons<ChevronRight className="w-4 h-4 ml-1" /></Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </>
      )}
    </div>
  );

  const renderStep2 = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-500">Review and edit extracted data if needed</p>
        <Button variant="outline" size="sm" onClick={() => setEditing(!editing)} data-testid="edit-toggle">
          {editing ? <Save className="w-4 h-4 mr-2" /> : <Edit className="w-4 h-4 mr-2" />}{editing ? 'Done' : 'Edit'}
        </Button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Customer Info */}
        <Card className="shadow-sm border-slate-200">
          <CardHeader className="pb-3 bg-slate-50/50 border-b"><CardTitle className="text-sm font-medium text-slate-500">Customer Information</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div>
              <div className="flex items-center justify-between mb-1"><Label className="text-xs text-slate-500">Customer Name</Label><Badge className={`text-xs ${getConfidenceClass(extractedData?.customer_name_confidence)}`}>{getConfidenceText(extractedData?.customer_name_confidence)}</Badge></div>
              <Input value={editedData?.customer_name || ''} onChange={(e) => handleFieldChange('customer_name', e.target.value)} disabled={!editing} className="bg-white" data-testid="field-customer-name" />
            </div>
            <div>
              <div className="flex items-center justify-between mb-1"><Label className="text-xs text-slate-500">NMI</Label><Badge className={`text-xs ${getConfidenceClass(extractedData?.nmi_confidence)}`}>{getConfidenceText(extractedData?.nmi_confidence)}</Badge></div>
              <Input value={editedData?.nmi || ''} onChange={(e) => handleFieldChange('nmi', e.target.value)} disabled={!editing} className="bg-white font-mono" data-testid="field-nmi" />
            </div>
            <div>
              <div className="flex items-center justify-between mb-1"><Label className="text-xs text-slate-500">Site Address</Label><Badge className={`text-xs ${getConfidenceClass(extractedData?.site_address_confidence)}`}>{getConfidenceText(extractedData?.site_address_confidence)}</Badge></div>
              <Input value={editedData?.site_address || ''} onChange={(e) => handleFieldChange('site_address', e.target.value)} disabled={!editing} className="bg-white" />
            </div>
            <div>
              <div className="flex items-center justify-between mb-1"><Label className="text-xs text-slate-500">Retailer</Label><Badge className={`text-xs ${getConfidenceClass(extractedData?.retailer_name_confidence)}`}>{getConfidenceText(extractedData?.retailer_name_confidence)}</Badge></div>
              <Input value={editedData?.retailer_name || ''} onChange={(e) => handleFieldChange('retailer_name', e.target.value)} disabled={!editing} className="bg-white" />
            </div>
          </CardContent>
        </Card>
        {/* Usage Info */}
        <Card className="shadow-sm border-slate-200">
          <CardHeader className="pb-3 bg-slate-50/50 border-b"><CardTitle className="text-sm font-medium text-slate-500">Usage Information</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div>
              <div className="flex items-center justify-between mb-1"><Label className="text-xs text-slate-500">Total kWh</Label><Badge className={`text-xs ${getConfidenceClass(extractedData?.usage_confidence)}`}>{getConfidenceText(extractedData?.usage_confidence)}</Badge></div>
              <Input type="number" value={editedData?.total_kwh || ''} onChange={(e) => handleFieldChange('total_kwh', parseFloat(e.target.value) || 0)} disabled={!editing} className="bg-white" data-testid="field-total-kwh" />
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div><Label className="text-xs text-slate-500">Peak kWh</Label><Input type="number" value={editedData?.peak_kwh || ''} onChange={(e) => handleFieldChange('peak_kwh', parseFloat(e.target.value) || 0)} disabled={!editing} className="bg-white" /></div>
              <div><Label className="text-xs text-slate-500">Shoulder kWh</Label><Input type="number" value={editedData?.shoulder_kwh || ''} onChange={(e) => handleFieldChange('shoulder_kwh', parseFloat(e.target.value) || 0)} disabled={!editing} className="bg-white" /></div>
              <div><Label className="text-xs text-slate-500">Off-Peak kWh</Label><Input type="number" value={editedData?.off_peak_kwh || ''} onChange={(e) => handleFieldChange('off_peak_kwh', parseFloat(e.target.value) || 0)} disabled={!editing} className="bg-white" /></div>
            </div>
            <div>
              <Label className="text-xs text-slate-500">Supply Days</Label>
              <Input type="number" value={editedData?.supply_days || ''} onChange={(e) => handleFieldChange('supply_days', parseInt(e.target.value) || 0)} disabled={!editing} className="bg-white" data-testid="field-supply-days" />
            </div>
            <div>
              <div className="flex items-center justify-between mb-1"><Label className="text-xs text-slate-500">Total Bill Amount</Label><Badge className={`text-xs ${getConfidenceClass(extractedData?.total_bill_confidence)}`}>{getConfidenceText(extractedData?.total_bill_confidence)}</Badge></div>
              <Input type="number" step="0.01" value={editedData?.total_bill_amount || ''} onChange={(e) => handleFieldChange('total_bill_amount', parseFloat(e.target.value) || 0)} disabled={!editing} className="bg-white" data-testid="field-total-bill" />
            </div>
          </CardContent>
        </Card>
        {/* Rates Info */}
        <Card className="shadow-sm border-slate-200">
          <CardHeader className="pb-3 bg-slate-50/50 border-b"><CardTitle className="text-sm font-medium text-slate-500">Rate Information (from bill)</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div><Label className="text-xs text-slate-500">Daily Supply Charge ($/day)</Label><Input type="number" step="0.01" value={editedData?.daily_supply_charge || ''} onChange={(e) => handleFieldChange('daily_supply_charge', parseFloat(e.target.value) || 0)} disabled={!editing} className="bg-white" /></div>
            <div><Label className="text-xs text-slate-500">Flat Usage Rate ($/kWh)</Label><Input type="number" step="0.001" value={editedData?.usage_rate_flat || ''} onChange={(e) => handleFieldChange('usage_rate_flat', parseFloat(e.target.value) || 0)} disabled={!editing} className="bg-white" /></div>
            <div className="grid grid-cols-3 gap-2">
              <div><Label className="text-xs text-slate-500">Peak Rate</Label><Input type="number" step="0.001" value={editedData?.usage_rate_peak || ''} onChange={(e) => handleFieldChange('usage_rate_peak', parseFloat(e.target.value) || 0)} disabled={!editing} className="bg-white" /></div>
              <div><Label className="text-xs text-slate-500">Shoulder Rate</Label><Input type="number" step="0.001" value={editedData?.usage_rate_shoulder || ''} onChange={(e) => handleFieldChange('usage_rate_shoulder', parseFloat(e.target.value) || 0)} disabled={!editing} className="bg-white" /></div>
              <div><Label className="text-xs text-slate-500">Off-Peak Rate</Label><Input type="number" step="0.001" value={editedData?.usage_rate_off_peak || ''} onChange={(e) => handleFieldChange('usage_rate_off_peak', parseFloat(e.target.value) || 0)} disabled={!editing} className="bg-white" /></div>
            </div>
          </CardContent>
        </Card>
        {/* Retailer Discount - NEW */}
        <Card className="shadow-sm border-amber-200 bg-amber-50/50">
          <CardHeader className="pb-3 border-b border-amber-200"><CardTitle className="text-sm font-medium text-amber-800">Customer's Current Discount</CardTitle><CardDescription className="text-xs text-amber-700">Edit if the PDF didn't detect the right discount</CardDescription></CardHeader>
          <CardContent className="space-y-4">
            <div><Label className="text-xs text-slate-500">Discount Amount ($)</Label><Input type="number" step="0.01" value={retailerDiscountAmount} onChange={(e) => setRetailerDiscountAmount(parseFloat(e.target.value) || 0)} className="bg-white" data-testid="retailer-discount-amount" /></div>
            <div><Label className="text-xs text-slate-500">Discount Applies To</Label>
              <Select value={retailerDiscountScope} onValueChange={setRetailerDiscountScope}>
                <SelectTrigger className="bg-white" data-testid="retailer-discount-scope"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="usage_and_supply">Usage & Supply</SelectItem>
                  <SelectItem value="usage_only">Usage Only</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-500 mt-1">Note: Demand/network charges are never discounted</p>
            </div>
            <div><Label className="text-xs text-slate-500">Discount Description</Label><Input value={editedData?.discounts || ''} onChange={(e) => handleFieldChange('discounts', e.target.value)} disabled={!editing} className="bg-white" placeholder="e.g., Pay on time discount" /></div>
          </CardContent>
        </Card>
        {/* Solar Export from Bill */}
        <Card className="shadow-sm border-green-200 bg-green-50/30">
          <CardHeader className="pb-3 border-b border-green-200"><CardTitle className="text-sm font-medium text-green-800">Solar Export (from bill)</CardTitle><CardDescription className="text-xs text-green-700">If the customer has solar, enter the export data from their bill</CardDescription></CardHeader>
          <CardContent className="space-y-4">
            <div><Label className="text-xs text-slate-500">Solar Export kWh</Label><Input type="number" step="0.01" value={editedData?.solar_feed_in_kwh || ''} onChange={(e) => handleFieldChange('solar_feed_in_kwh', parseFloat(e.target.value) || 0)} disabled={!editing} className="bg-white" placeholder="0" data-testid="field-solar-kwh" /></div>
            <div><Label className="text-xs text-slate-500">Retailer Feed-in Rate (c/kWh)</Label><Input type="number" step="0.1" value={editedData?.solar_feed_in_rate ? (editedData.solar_feed_in_rate * 100).toFixed(1) : ''} onChange={(e) => handleFieldChange('solar_feed_in_rate', (parseFloat(e.target.value) || 0) / 100)} disabled={!editing} className="bg-white" placeholder="e.g., 5.0" data-testid="field-solar-rate" /></div>
          </CardContent>
        </Card>
      </div>
      <div className="flex gap-4">
        <Button variant="outline" onClick={() => setStep(1)} className="rounded-full"><ChevronLeft className="w-5 h-5 mr-2" />Back</Button>
        <Button onClick={() => setStep(3)} className="flex-1 rounded-full bg-primary hover:bg-primary/90 h-12" data-testid="continue-to-plan-btn">Continue to Tariff & Plan Selection<ChevronRight className="w-5 h-5 ml-2" /></Button>
      </div>
    </div>
  );

  const renderStep3 = () => {
    const selectedTariffData = tariffs.find(t => t.id === selectedTariff);
    const selectedPlanData = plans.find(p => p.id === selectedPlan);
    
    return (
      <div className="space-y-6">
        {/* Tariff Selection */}
        <Card>
          <CardHeader><CardTitle>1. Select Tariff</CardTitle><CardDescription>Choose the tariff code that matches the customer's network/meter type</CardDescription></CardHeader>
          <CardContent className="space-y-4">
            {tariffs.length > 0 ? (
              <>
                <Popover open={tariffOpen} onOpenChange={setTariffOpen}>
                  <PopoverTrigger asChild>
                    <Button variant="outline" role="combobox" aria-expanded={tariffOpen} className="w-full h-12 justify-between font-normal" data-testid="tariff-select">
                      {selectedTariff ? (() => {
                        const t = tariffs.find(t => t.id === selectedTariff);
                        return t ? <span><span className="font-mono font-bold">{t.tariff_code}</span> - {t.tariff_name}</span> : 'Select a tariff...';
                      })() : 'Search tariffs...'}
                      <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
                    <Command>
                      <CommandInput placeholder="Search tariff code, name, network..." data-testid="tariff-search-input" />
                      <CommandList>
                        <CommandEmpty>No tariff found.</CommandEmpty>
                        <CommandGroup>
                          {tariffs.map((t) => (
                            <CommandItem key={t.id} value={`${t.tariff_code} ${t.tariff_name} ${t.network_distributor || ''} ${t.state || ''}`} onSelect={() => { setSelectedTariff(t.id); setTariffOpen(false); }}>
                              <Check className={`mr-2 h-4 w-4 ${selectedTariff === t.id ? 'opacity-100' : 'opacity-0'}`} />
                              <div className="flex items-center gap-2 flex-1">
                                <span className="font-mono font-bold">{t.tariff_code}</span>
                                <span className="text-sm">{t.tariff_name}</span>
                                <Badge variant="outline" className="text-xs capitalize ml-auto">{t.tariff_type}</Badge>
                                {t.state && <Badge variant="secondary" className="text-xs">{t.state}</Badge>}
                              </div>
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
                {selectedTariffData && (
                  <div className="p-4 bg-slate-50 rounded-lg text-sm">
                    <div className="grid grid-cols-2 gap-2">
                      <div><span className="text-slate-500">Supply:</span> <span className="font-medium">${selectedTariffData.supply_charge_daily?.toFixed(4)}/day</span></div>
                      {selectedTariffData.usage_rate_flat && <div><span className="text-slate-500">Flat Rate:</span> <span className="font-medium">${selectedTariffData.usage_rate_flat?.toFixed(4)}/kWh</span></div>}
                      {selectedTariffData.usage_rate_peak && <div><span className="text-slate-500">Peak:</span> <span className="font-medium">${selectedTariffData.usage_rate_peak?.toFixed(4)}/kWh</span></div>}
                      {selectedTariffData.usage_rate_shoulder && <div><span className="text-slate-500">Shoulder:</span> <span className="font-medium">${selectedTariffData.usage_rate_shoulder?.toFixed(4)}/kWh</span></div>}
                      {selectedTariffData.usage_rate_off_peak && <div><span className="text-slate-500">Off-Peak:</span> <span className="font-medium">${selectedTariffData.usage_rate_off_peak?.toFixed(4)}/kWh</span></div>}
                      {selectedTariffData.demand_rate && <div><span className="text-slate-500">Demand:</span> <span className="font-medium">${selectedTariffData.demand_rate?.toFixed(2)}/kW</span></div>}
                    </div>
                    <p className="text-xs text-slate-500 mt-2">Rates shown are before plan discount</p>
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-8 bg-amber-50 rounded-lg border border-amber-200">
                <AlertCircle className="w-8 h-8 text-amber-600 mx-auto mb-2" />
                <p className="text-amber-800 font-medium">No tariffs available</p>
                <p className="text-sm text-amber-600 mt-1">Please ask an admin to upload tariffs first</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Plan Selection */}
        <Card>
          <CardHeader><CardTitle>2. Select Plan</CardTitle><CardDescription>Choose the discount plan for this customer</CardDescription></CardHeader>
          <CardContent className="space-y-4">
            {plans.length > 0 ? (
              <>
                <Select value={selectedPlan} onValueChange={setSelectedPlan}>
                  <SelectTrigger className="h-12" data-testid="plan-select"><SelectValue placeholder="Select a plan" /></SelectTrigger>
                  <SelectContent>
                    {plans.map((p) => (
                      <SelectItem key={p.id} value={p.id}>
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{p.plan_name}</span>
                          <Badge className="bg-green-100 text-green-800">{p.discount_percentage}% off</Badge>
                          <Badge variant="outline" className="text-xs">{p.discount_scope === 'usage_only' ? 'Usage only' : 'Usage & Supply'}</Badge>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {selectedPlanData && (
                  <div className="p-4 bg-green-50 rounded-lg text-sm border border-green-200">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-2xl font-bold text-green-700">{selectedPlanData.discount_percentage}%</span>
                      <span className="text-green-700">discount</span>
                    </div>
                    <p className="text-green-800">{selectedPlanData.description}</p>
                    {selectedPlanData.conditions && <p className="text-xs text-green-600 mt-1">Conditions: {selectedPlanData.conditions}</p>}
                    <p className="text-xs text-green-600 mt-2">
                      Discount applies to: <strong>{selectedPlanData.discount_scope === 'usage_only' ? 'Usage charges only' : 'Usage & Supply charges'}</strong>
                    </p>
                    <p className="text-xs text-slate-500 mt-1">Note: Demand charges are never discounted (network fees)</p>
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-8 bg-amber-50 rounded-lg border border-amber-200">
                <AlertCircle className="w-8 h-8 text-amber-600 mx-auto mb-2" />
                <p className="text-amber-800 font-medium">No plans available</p>
                <p className="text-sm text-amber-600 mt-1">Please ask an admin to create plans first</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Options */}
        <Card>
          <CardHeader><CardTitle>3. Comparison Options</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div><Label className="font-medium">Include Demand Charges</Label><p className="text-sm text-slate-500">Include demand tariff component in comparison</p></div>
              <Switch checked={includeDemand} onCheckedChange={setIncludeDemand} className="data-[state=unchecked]:bg-slate-300" data-testid="include-demand-switch" />
            </div>
          </CardContent>
        </Card>

        {/* Solar & Greenpower */}
        <Card className="shadow-sm border-green-200">
          <CardHeader className="border-b border-green-200"><CardTitle className="text-green-800">4. Solar & GreenPower</CardTitle><CardDescription>Optional: Configure solar feed-in and GreenPower for the NBE offer</CardDescription></CardHeader>
          <CardContent className="space-y-5">
            {(editedData?.solar_feed_in_kwh > 0) && (
              <div className="space-y-2">
                <Label className="font-medium">NBE Solar Feed-in Rate (c/kWh)</Label>
                <p className="text-xs text-slate-500">Customer exports {editedData?.solar_feed_in_kwh?.toFixed(2)} kWh — enter NBE's feed-in rate in cents to calculate their solar credit</p>
                <Input type="number" step="0.1" value={nbeSolarRate || ''} onChange={(e) => setNbeSolarRate(parseFloat(e.target.value) || 0)} className="bg-white" placeholder="e.g., 5.0" data-testid="nbe-solar-rate" />
                {nbeSolarRate > 0 && (
                  <p className="text-sm text-green-700 font-medium">Estimated NBE solar credit: ${(editedData.solar_feed_in_kwh * nbeSolarRate / 100).toFixed(2)}</p>
                )}
              </div>
            )}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div><Label className="font-medium">GreenPower 100%</Label><p className="text-xs text-slate-500">Add GreenPower charge to NBE offer (applied to all usage kWh)</p></div>
                <Switch checked={includeGreenpower} onCheckedChange={setIncludeGreenpower} className="data-[state=unchecked]:bg-slate-300" data-testid="include-greenpower-switch" />
              </div>
              {includeGreenpower && (
                <div className="space-y-2">
                  <Label className="text-xs text-slate-500">GreenPower Rate (c/kWh)</Label>
                  <Input type="number" step="0.1" value={greenpowerRate || ''} onChange={(e) => setGreenpowerRate(parseFloat(e.target.value) || 0)} className="bg-white" placeholder="e.g., 5.5" data-testid="greenpower-rate" />
                  {greenpowerRate > 0 && editedData?.total_kwh > 0 && (
                    <p className="text-sm text-green-700 font-medium">Estimated GreenPower cost: ${(editedData.total_kwh * greenpowerRate / 100).toFixed(2)}</p>
                  )}
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Metering Charge */}
        <Card className="shadow-sm border-blue-200">
          <CardHeader className="border-b border-blue-200"><CardTitle className="text-blue-800">5. Metering Charge</CardTitle><CardDescription>Optional: Add a monthly metering charge to the NBE offer (e.g., COMMS4)</CardDescription></CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div><Label className="font-medium">Include Metering Charge</Label><p className="text-xs text-slate-500">Monthly flat fee for metering services (inc GST)</p></div>
              <Switch checked={includeMetering} onCheckedChange={setIncludeMetering} className="data-[state=unchecked]:bg-slate-300" data-testid="include-metering-switch" />
            </div>
            {includeMetering && (
              <div className="space-y-2">
                <Label className="text-xs text-slate-500">Monthly Metering Charge ($ inc GST)</Label>
                <Input type="number" step="0.01" value={meteringChargeMonthly} onChange={(e) => setMeteringChargeMonthly(parseFloat(e.target.value) || 0)} className="bg-white" placeholder="e.g., 77.00" data-testid="metering-charge-input" />
                {meteringChargeMonthly > 0 && editedData?.supply_days > 0 && (
                  <p className="text-sm text-blue-700 font-medium">
                    Estimated metering cost for {editedData.supply_days} days: ${(meteringChargeMonthly * editedData.supply_days / 30).toFixed(2)}
                  </p>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        <div className="flex gap-4">
          <Button variant="outline" onClick={() => setStep(2)} className="rounded-full"><ChevronLeft className="w-5 h-5 mr-2" />Back</Button>
          <Button onClick={generateComparison} disabled={!selectedTariff || !selectedPlan || loading} className="flex-1 rounded-full bg-primary hover:bg-primary/90 h-12" data-testid="generate-comparison-btn">
            {loading ? <><Loader2 className="w-5 h-5 mr-2 animate-spin" />Generating...</> : <>Generate Comparison<ChevronRight className="w-5 h-5 ml-2" /></>}
          </Button>
        </div>
      </div>
    );
  };

  const renderStep4 = () => {
    const result = comparisonResult?.comparison_result;
    const assumptions = result?.assumptions || [];
    const retailerBreakdown = result?.retailer_breakdown || [];
    const nbeBreakdown = result?.nbe_breakdown || [];
    
    return (
      <div className="space-y-6">
        <Card className="gradient-success overflow-hidden">
          <CardContent className="p-8 text-center">
            <p className="text-green-900 font-medium mb-2">Estimated Savings</p>
            <p className="text-5xl font-bold text-green-900" data-testid="savings-amount">{formatCurrency(result?.savings_amount)}</p>
            <p className="text-green-800 mt-2">{(result?.savings_percentage || 0).toFixed(1)}% less than current retailer</p>
          </CardContent>
        </Card>
        
        {/* Side by side detailed breakdowns */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Current Retailer Breakdown */}
          <Card>
            <CardHeader className="bg-slate-50 border-b">
              <CardTitle className="text-lg">Current Retailer</CardTitle>
              <CardDescription>Breakdown from customer's bill</CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              <div className="divide-y">
                {retailerBreakdown.map((item, idx) => (
                  <div key={idx} className={`p-4 ${item.category === 'Total' ? 'bg-slate-100 font-semibold' : ''} ${item.category === 'Discounts' ? 'text-green-700' : ''}`}>
                    <div className="flex justify-between items-start mb-1">
                      <span className="text-sm text-slate-600">{item.description}</span>
                      <span className={`font-mono ${item.amount < 0 ? 'text-green-600' : ''}`}>
                        {item.amount < 0 ? '-' : ''}{formatCurrency(Math.abs(item.amount))}
                      </span>
                    </div>
                    <p className="text-xs text-slate-500 font-mono">{item.formula}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
          
          {/* NBE Proposed Breakdown */}
          <Card className="border-primary/30">
            <CardHeader className="bg-primary/5 border-b border-primary/20">
              <CardTitle className="text-lg text-primary">Next Business Energy</CardTitle>
              <CardDescription>Proposed offer breakdown</CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              <div className="divide-y">
                {nbeBreakdown.map((item, idx) => (
                  <div key={idx} className={`p-4 ${item.category === 'Total' ? 'bg-primary/10 font-semibold' : ''} ${item.category === 'Discounts' ? 'text-green-700' : ''}`}>
                    <div className="flex justify-between items-start mb-1">
                      <span className="text-sm text-slate-600">{item.description}</span>
                      <span className={`font-mono ${item.amount < 0 ? 'text-green-600' : ''}`}>
                        {item.amount < 0 ? '-' : ''}{formatCurrency(Math.abs(item.amount))}
                      </span>
                    </div>
                    <p className="text-xs text-slate-500 font-mono">{item.formula}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
        
        {/* Summary comparison table */}
        <Card>
          <CardHeader><CardTitle>Summary</CardTitle></CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead><tr className="border-b"><th className="text-left py-3 px-4 font-medium text-slate-500">Charge Type</th><th className="text-right py-3 px-4 font-medium text-slate-500">Current Retailer</th><th className="text-right py-3 px-4 font-medium text-primary">NBE Offer</th><th className="text-right py-3 px-4 font-medium text-green-600">Savings</th></tr></thead>
                <tbody>
                  <tr className="border-b"><td className="py-3 px-4">Supply Charges</td><td className="text-right py-3 px-4">{formatCurrency(result?.retailer_supply_cost)}</td><td className="text-right py-3 px-4">{formatCurrency(result?.our_supply_cost)}</td><td className="text-right py-3 px-4 text-green-600">{formatCurrency((result?.retailer_supply_cost || 0) - (result?.our_supply_cost || 0))}</td></tr>
                  <tr className="border-b"><td className="py-3 px-4">Usage Charges</td><td className="text-right py-3 px-4">{formatCurrency(result?.retailer_usage_cost)}</td><td className="text-right py-3 px-4">{formatCurrency(result?.our_usage_cost)}</td><td className="text-right py-3 px-4 text-green-600">{formatCurrency((result?.retailer_usage_cost || 0) - (result?.our_usage_cost || 0))}</td></tr>
                  <tr className="border-b"><td className="py-3 px-4">Demand Charges</td><td className="text-right py-3 px-4">{formatCurrency(result?.retailer_demand_cost)}</td><td className="text-right py-3 px-4">{formatCurrency(result?.our_demand_cost)}</td><td className="text-right py-3 px-4 text-green-600">{formatCurrency((result?.retailer_demand_cost || 0) - (result?.our_demand_cost || 0))}</td></tr>
                  <tr className="border-b"><td className="py-3 px-4">Discounts</td><td className="text-right py-3 px-4 text-green-600">-{formatCurrency(result?.retailer_discount)}</td><td className="text-right py-3 px-4 text-green-600">-{formatCurrency(result?.our_discount)}</td><td className="text-right py-3 px-4">-</td></tr>
                  {result?.our_metering_cost > 0 && (
                    <tr className="border-b"><td className="py-3 px-4">Metering Charge</td><td className="text-right py-3 px-4">-</td><td className="text-right py-3 px-4">{formatCurrency(result?.our_metering_cost)}</td><td className="text-right py-3 px-4">-</td></tr>
                  )}
                  <tr className="bg-slate-50 font-semibold"><td className="py-3 px-4">Total</td><td className="text-right py-3 px-4">{formatCurrency(result?.retailer_total)}</td><td className="text-right py-3 px-4 text-primary">{formatCurrency(result?.our_total)}</td><td className="text-right py-3 px-4 text-green-600">{formatCurrency(result?.savings_amount)}</td></tr>
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
        
        {assumptions.length > 0 && (
          <Card className="bg-amber-50 border-amber-200">
            <CardHeader className="pb-2"><CardTitle className="text-amber-800 text-base flex items-center gap-2"><AlertCircle className="w-5 h-5" />Assumptions & Notes</CardTitle></CardHeader>
            <CardContent><ul className="list-disc list-inside space-y-1 text-sm text-amber-900">{assumptions.map((a, i) => <li key={i}>{a}</li>)}</ul></CardContent>
          </Card>
        )}
        <div className="flex gap-4">
          <Button variant="outline" onClick={() => navigate('/dashboard')} className="rounded-full">Back to Dashboard</Button>
          <Button variant="outline" onClick={emailComparison} className="rounded-full" data-testid="email-comparison-btn"><Mail className="w-5 h-5 mr-2" />Email</Button>
          <Button onClick={downloadReport} className="flex-1 rounded-full bg-primary hover:bg-primary/90 h-12" data-testid="download-report-btn"><Download className="w-5 h-5 mr-2" />Download PDF Report</Button>
        </div>
        <div className="flex justify-center"><Button variant="link" onClick={() => navigate(`/comparison/${comparisonResult.id}`)} className="text-primary">View Full Comparison Details</Button></div>
      </div>
    );
  };

  return (
    <Layout>
      <div className="max-w-4xl mx-auto" data-testid="new-comparison-page">
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            {STEPS.map((s, idx) => (
              <div key={s.id} className="flex items-center">
                <div className={`flex items-center gap-2 ${step >= s.id ? 'text-primary' : 'text-slate-400'}`}>
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${step > s.id ? 'bg-primary text-white' : step === s.id ? 'bg-primary/10 text-primary border-2 border-primary' : 'bg-slate-100 text-slate-400'}`}>
                    {step > s.id ? <Check className="w-5 h-5" /> : s.id}
                  </div>
                  <div className="hidden sm:block"><p className="text-sm font-medium">{s.title}</p><p className="text-xs text-slate-500">{s.description}</p></div>
                </div>
                {idx < STEPS.length - 1 && <div className={`w-12 lg:w-24 h-0.5 mx-2 ${step > s.id ? 'bg-primary' : 'bg-slate-200'}`} />}
              </div>
            ))}
          </div>
          <Progress value={(step / STEPS.length) * 100} className="h-2" />
        </div>
        <Card className="shadow-lg">
          <CardHeader><CardTitle>{STEPS[step - 1].title}</CardTitle><CardDescription>{STEPS[step - 1].description}</CardDescription></CardHeader>
          <CardContent>
            {step === 1 && renderStep1()}
            {step === 2 && renderStep2()}
            {step === 3 && renderStep3()}
            {step === 4 && renderStep4()}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
